import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { GenerateRequest, JobSnapshot } from "../api/types";

/**
 * All known jobs on the server (running + recently-completed).
 *
 * Polls every 2 seconds — enough to feel live without hammering the
 * backend. When any job in the list is still running, we poll faster.
 * Server keeps finished jobs for ~10 minutes so refreshing the page or
 * navigating away never loses the last run.
 */
export function useJobs() {
  return useQuery<JobSnapshot[]>({
    queryKey: ["jobs"],
    queryFn: api.generate.listJobs,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.length === 0) return 4000;
      const anyRunning = data.some((j) => j.is_running);
      return anyRunning ? 1500 : 5000;
    },
    refetchIntervalInBackground: true,
  });
}

/** POST a new generation job and auto-refresh the list. */
export function useStartJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateRequest) => api.generate.start(body),
    onSuccess: () => {
      // Pull the list immediately so the new job appears without delay.
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}
